import QRCode from 'qrcode';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import { Button, Card, Field, Icon, Input } from '../../design';
import s from './auth.module.css';

/** `/invitatie/:token`: parola → secretul TOTP (QR + text, o singură dată) → codul → contul e activ. */
export function InvitationPage() {
  const { token = '' } = useParams();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [setup, setSetup] = useState<{ otpauthUri: string; secret: string } | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!setup) return;
    QRCode.toDataURL(setup.otpauthUri, { margin: 1, width: 220 })
      .then(setQr)
      .catch(() => setQr(null));
  }, [setup]);

  const accept = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('Parolele nu coincid.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await unwrap(
        api.POST('/auth/invitations/{token}/accept', {
          params: { path: { token } },
          body: { password },
        }),
      );
      setSetup(r.totpSetup);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await unwrap(
        api.POST('/auth/invitations/{token}/verify-totp', {
          params: { path: { token } },
          body: { code },
        }),
      );
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={s.page}>
      <Card className={s.card}>
        <div className={s.brand}>
          <span className={s.mark}>
            <Icon name="locker" size={22} />
          </span>
          <div>
            <strong>SYNAPTIC</strong>
            <span className="muted small">Activarea contului</span>
          </div>
        </div>
        {done ? (
          <div className="stack">
            <h1>Contul este activ</h1>
            <p className="muted">
              Autentificarea în doi pași este configurată. Te poți conecta cu parola și codul din
              aplicație.
            </p>
            <Link to="/autentificare">
              <Button variant="primary">Către autentificare</Button>
            </Link>
          </div>
        ) : setup === null ? (
          <form onSubmit={accept} className="stack">
            <h1>Alege parola</h1>
            <p className="muted">
              Minimum 12 caractere. După parolă configurezi aplicația de autentificare (TOTP),
              obligatorie pentru toți utilizatorii.
            </p>
            <Field label="Parolă" htmlFor="inv-password">
              <Input
                id="inv-password"
                type="password"
                autoComplete="new-password"
                minLength={12}
                maxLength={128}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
            </Field>
            <Field label="Repetă parola" htmlFor="inv-confirm">
              <Input
                id="inv-confirm"
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </Field>
            {error && (
              <p className={s.error} role="alert">
                {error}
              </p>
            )}
            <Button variant="primary" type="submit" busy={busy}>
              Continuă
            </Button>
          </form>
        ) : (
          <form onSubmit={verify} className="stack">
            <h1>Aplicația de autentificare</h1>
            <p className="muted">
              Scanează codul QR în Google Authenticator, Microsoft Authenticator sau 1Password, apoi
              introdu codul afișat. Secretul se arată o singură dată.
            </p>
            <div className={s.qr}>
              {qr ? (
                <img
                  src={qr}
                  alt="Cod QR pentru aplicația de autentificare"
                  width={220}
                  height={220}
                />
              ) : (
                <span className="muted">Se generează…</span>
              )}
            </div>
            <div className={s.secret} aria-label="Secret TOTP">
              {setup.secret}
            </div>
            <Field label="Codul din aplicație" htmlFor="inv-code">
              <Input
                id="inv-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
            </Field>
            {error && (
              <p className={s.error} role="alert">
                {error}
              </p>
            )}
            <Button variant="primary" type="submit" busy={busy} disabled={code.length !== 6}>
              Activează contul
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
